<script lang="ts">
	// The design system's MenuTile (design/components/core/MenuTile.jsx): one
	// item on the sell screen, and each tap adds one.
	//
	// The press is the receipt: the tile inverts the instant a finger lands
	// and nothing moves. The height is fixed (--tile-h) and the name clamped to
	// two lines, so neither a press nor a long name can shift the grid under
	// the next tap. Sold out takes the one disabled look, and the price slot
	// says so in words, in the same line box, so the tile keeps its shape.
	//
	// The grid it sits in is the page's: auto-fill, minmax(--tile-min, 1fr),
	// --touch-gap apart.

	import { pressable } from './pressable';

	interface Props {
		testId: string;
		name: string;
		/** Already formatted, Latin digits. */
		price: string;
		onAdd: () => void;
		soldOut?: boolean;
		soldOutLabel: string;
		/**
		 * Takes no taps for a reason other than sold out, such as a sale
		 * waiting to be confirmed. The price stays, so it never reads as sold out.
		 */
		disabled?: boolean;
	}

	let {
		testId,
		name,
		price,
		onAdd,
		soldOut = false,
		soldOutLabel,
		disabled = false,
	}: Props = $props();
</script>

<button
	type="button"
	class="tile"
	data-testid={testId}
	data-press="invert"
	disabled={soldOut || disabled}
	onclick={onAdd}
	{@attach pressable}
>
	<span class="name">{name}</span>
	<span class="price" class:words={soldOut}>
		{#if soldOut}
			{soldOutLabel}
		{:else}
			<bdi dir="ltr">{price}</bdi>
		{/if}
	</span>
</button>

<style>
	.tile {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		block-size: var(--tile-h);
		min-inline-size: 0;
		padding: 12px 14px;
		text-align: start;
		background: var(--surface-card);
		color: var(--text-strong);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-tile);
		font-family: var(--font-sans);
		cursor: pointer;
	}

	.name {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		overflow: hidden;
		font-size: var(--text-body-sm);
		font-weight: 600;
		line-height: 1.4;
		color: var(--text-strong);
	}

	/* A fixed line box, so the sold-out word and the price take exactly the
	 * same space. */
	.price {
		display: block;
		block-size: 22px;
		line-height: 22px;
		font-size: 15px;
		font-weight: 600;
		color: var(--text-body);
		font-family: var(--font-mono);
	}

	.price.words {
		font-family: var(--font-sans);
	}
</style>
